<template>
  <div id="CampaignEditSegmentationList">

    <Page
      :title="`${$t('Editing')} '${campaign.name}'`"
      :secondaryActions="[{
        label: $t('Notes'),
        onAction: () => { eventHub.$emit('showCampaignNotes', campaign.id) }
      }]"
      :primaryAction="{
        label: $t('Add Segment'),
        onAction: () => createNewSegment(),
        disabled: saving
      }"
      size="fullWidth"
    >    
      
      <campaign-edit-header :campaign="campaign"></campaign-edit-header>
      <LoadingLayout v-if="loading"/>
      <Layout v-if="!loading">
        <LayoutSection>
          <Card 
            :title="$t('Segments')"
          >
            <Filters
              :filters="filters"
              :query.sync="query"
              :applied-filters="appliedFilters"
              @update:applied-filters="setAppliedFilters"
              @update:query="setQuery"
            />
            <DataTable
              :columns="[
                {
                  key: 'name',
                  label: $t('Name'),
                  sortable: true, 
                  fixed: true,
                  minWidth: 250
                },
                {
                  key: 'typeLabel',
                  label: $t('Type'),
                  sortable: true
                },
                {
                  key: 'statusLabel',
                  label: $t('Status'),
                  sortable: true
                },
                {
                  key: 'updatedOn',
                  label: $t('Updated'),
                  format: 'dateShort',
                  sortable: true
                },
                {
                  key: 'options',
                  label: $t('Options'),
                  sortable: false
                },
                {
                  key: 'widget',
                  label: $t('Widget'),
                  sortable: true, 
                  minWidth: 220,
                  wrap: true
                },
                {
                  key: 'numOffers',
                  label: $t('# Offers'),
                  sortable: true
                }
              ]"
              :rows="formattedSegments"
              :row-actions="row => ([
                {
                  label: $t('Copy'),
                  onAction: (segment) => showCopyModal(segment),
                  disabled: saving
                },
                {
                  label: row.status === 'active' ? $t('Disable') : $t('Enable'),
                  onAction: (segment) => toggleSegment(segment),
                  disabled: saving
                },
                {
                  label: $t('Delete'),
                  onAction: (segment) => deleteSegment(segment),
                  disabled: saving
                }
              ])"
              :sort.sync="sortBy"
              :bulk-actions="bulkActions"
              :selected-ids.sync="selectedIds"
              :pagination="{
                offset: offset,
                limit: limit,
                total: formattedSegments.length,
                updateOffset: val => offset = val,
                updateLimit: updateLimit
              }"
              compact
            >
              <template v-slot:col:name="{row}">
                <!-- <Link :url="getSegmentUrl(row)">{{row.name}}</Link> -->
                <PageLink :href="getSegmentUrl(row)">{{row.name}}</PageLink>
              </template>
              <template v-slot:col:options="{row}">
                <template v-if="row.options.length">
                  <Badge
                    v-for="(opt, index) in row.options"
                    :key="index"
                    size="small"
                  >{{opt.label}}</Badge>
                </template>
                <template v-else>—</template>
              </template>
              <template v-slot:col:widget="{row}">
                <template v-if="row.type === 'include' || row.type === 'exit-intent'">
                  <template v-if="row.widgetId === -1 && offerFlyoutWidgetName">
                    <Badge size="small"><PageLink :href="`/creative/index.html#!/revisions/${offerFlyoutId}`" external>{{$t('Campaign Offer Flyout')}}</PageLink></Badge>
                  </template>
                  <template v-else-if="row.widget">
                    <!-- <ui-link :url="getWidgetUrl(row)">{{row.widget}}</ui-link> -->
                    <PageLink :href="`/creative/index.html#!/revisions/${row.widgetId}`" external>{{row.widget}}</PageLink>
                  </template>
                  <template v-else>—</template>
                </template>
                <template v-else>—</template>
              </template>
              <template v-slot:col:statusLabel="{row}">
                <Badge
                  :status="row.status === 'disabled' ? 'warning' : 'success'"
                >{{row.statusLabel}}</Badge>
              </template>
              <template v-slot:col:numOffers="{row}">
                <template v-if="row.numOffers">
                  <PageLink @click="showOffers(row)">{{row.numOffers}}</PageLink>
                </template>
                <template v-else>0</template>
              </template>
            </DataTable>
          </Card>
        </LayoutSection>
        <Modal
          :title="$t('Copy Segment')"
          :primaryAction="{
            type: 'primary',
            label: $t('Copy segment'),
            onAction: () => copySegment(false)
          }"
          :secondaryActions="[
            {
              label: $t('Cancel'),
              onAction: () => copySegmentId = null
            },
            {
              label: $t('Copy segment & edit'),
              onAction: () => copySegment(true)
            }
          ]"
          :visible="!!copySegmentId"
          @close="copySegmentId = null"
        >
          <p>{{$t('campaignEdit.segment.copySegmentMsg')}}</p><br>
          <FormLayout>
            <TextField
              :label="$t('Segment Name')"
              v-model="copySegmentName"
            />
            <Select
              :label="$t('Destination Campaign')"
              :options="campaignOptions"
              :searchable="true"
              v-model="copySegmentCampaignId"
            />
          </FormLayout>
        </Modal>
      </Layout>
    </Page>
  </div>
</template>

<script>
import _ from 'lodash'
import OffersUtil from '../classes/utils/OffersUtil'
import CampaignEditHeader from '@/components/CampaignEditHeader'

export default {
  name: 'CampaignEditSegmentationList',
  props: {
    campaign: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      loading: true,
      saving: false,
      segments: [],
      widgets: [],
      accountPrefs: null,
      authKey: null,
      campaigns: null,
      offset: 0,
      limit: 10,
      query: null,
      offerFlyoutId: null,
      offers: null,
      copySegmentId: null,
      copySegmentName: null,
      copySegmentCampaignId: null,
      sortBy: {
        column: 'name',
        direction: 'asc'
      },
      appliedFilters: {
        status: null,
        type: null,
        widget: null,
        option: null,
        offer: null
      },
      selectedIds: []
    }
  },
  mounted() {
    NProgress.start()

    this.setFilterFromQuery()

    this.$watch('$route', () => {
      this.setFilterFromQuery()
    }, { deep: true })

    this.limit = this.getTableLimit('tableLimit') || 10
    Promise.all([
      this.api.getSegments(this.campaign.id), 
      this.api.getWidgetsForSegmentation(),
      this.api.getIncentives(this.campaign.id),
      this.api.getCurrentAccountPrefs(),
      this.api.getCampaignNames()
    ])
      .then((data) => {
        const [ segments, widgets, offers, accountPrefs, campaigns ] = data
        this.segments = segments
        this.widgets = widgets
        // console.log(this.widgets)
        this.offers = OffersUtil.fillForFrontend(offers)
        this.accountPrefs = accountPrefs
        this.offerFlyoutId = this.campaign && this.campaign.widgetConfiguration && this.campaign.widgetConfiguration.smartWidgetId
        this.campaigns = campaigns
        this.loading = false
      })
      .catch(this.apiCatch)
      .finally(() => {
        NProgress.done()
      })
  },
  computed: {
    bulkActions() {
      const actions = [
        {
          label: this.$t('Enable'),
          onAction: () => this.handleBulkAction('enable')
        },
        {
          label: this.$t('Disable'),
          onAction: () => this.handleBulkAction('disable')
        }
      ]
      if (this.theUser.isSuper) {
        actions.push({
          label: this.$t('Edit Segments'),
          onAction: () => this.toBulkEditor()
        })
      }
      return actions
    },
    selectedIdsOffersWidgetsVisible() { 
      for (const id of this.selectedIds) {
        const seg = this.segments.find((s) => s.id === id)
        if (seg) {
          if (seg.type === 'exclude' || seg.type === 'display-none' || seg.type === 'ignore-user') {
            return false
          }
        }
      }
      return true
    },
    orderedOffers() {
      return _.orderBy(this.offers, [offer => offer.displayText && offer.displayText.trim().toLowerCase()], ['asc'])
    },
    attachedOffers() {
      const offers = {}
      this.selectedIds.forEach((id) => {
        const seg = this.segments.find((s) => s.id === id)
        if (seg) {
          seg.offerIds.forEach(oId => {
            offers[oId] = this.offers.find(o => o.id === oId)
          })
        }
      })
      let rv = Object.values(offers)
      return _.orderBy(rv, [offer => offer.displayText && offer.displayText.trim().toLowerCase()], ['asc'])
    },
    formattedWidgets() {
      const w = this.widgets.filter(w => {
        return w.type !== 'Smart'
      })
      .map(w => {
        return {
          label: w.name,
          value: w.id
        }
      })
      w.unshift({
        label: `- ${this.$t('Use Default')} -`,
        value: -1
      })
      return w
    },
    formattedSegments() {
      let rows = this.segments.map(s => {
        let typeLabel = this.$t(this.segmentTypeLabelMap[s.type])
         return { 
          ...s,
          typeLabel,
          options: this.getSegmentOptions(s),
          statusLabel: _.capitalize(s.status),
          widget: this.getWidgetName(s.widgetId),
          numOffers: this.offerIdsLength(s)
         }
      }).filter(s => {
        if (!this.query || !this.query.trim()) {
          return true
        }
        return s.name.toLowerCase().includes(this.query.trim().toLowerCase())
      })
      if (this.appliedFilters.status && this.appliedFilters.status.length) {
        rows = rows.filter(
          row => this.appliedFilters.status.indexOf(row.status) >= 0
        )
      }
      if (this.appliedFilters.type && this.appliedFilters.type.length) {
        rows = rows.filter(
          row => this.appliedFilters.type.indexOf(row.type) >= 0
        )
      }
      if (this.appliedFilters.widget && this.appliedFilters.widget.length) {
        rows = rows.filter(
          row => this.appliedFilters.widget.indexOf(row.widgetId) >= 0
        )
      }
      if (this.appliedFilters.option && this.appliedFilters.option.length) {
        rows = rows.filter(row => {
          return this.appliedFilters.option.some(optionValue => {
            return !!row.options.find(d => d.value === optionValue)
          })
        })
      }
      if (this.appliedFilters.offer) {
        rows = rows.filter(row => {
          return this.appliedFilters.offer.some(id => {
            return row.offerIds.indexOf(id) >= 0
          })
        })
      }
      if (this.sortBy) {
        rows = _.orderBy(rows, [
          (row) => String(row[this.sortBy.column]).toLowerCase()
        ], [this.sortBy.direction])
      }
      return rows
    },
    offerFlyoutWidgetName() {
      const {offerFlyoutId} = this
      const widget = this.widgets.find(d => d.id === offerFlyoutId)
      return widget && widget.name
    },
    filteredOfferIdMap() {
      return _(this.segments)
        .map(d => d.offerIds)
        .flatten()
        .uniq()
        .keyBy()
        .value()
    },
    filteredOffers() {
      const map = this.filteredOfferIdMap
      return this.offers.filter(d => map[d.id])
    },
    filteredWidgetIdMap() {
      return _(this.segments)
        .map(d => d.widgetId)
        .uniq()
        .keyBy()
        .value()
    },
    filteredWidgets() {
      const map = this.filteredWidgetIdMap
      return this.widgets.filter(d => map[d.id])
    },
    filters() {
      const offerFilters = this.filteredOffers.map(d => ({
        label: d.label,
        description: d.description,
        value: d.id
      }))
      
      const widgetFilters = this.filteredWidgets.map(d => ({
        label: d.name,
        description: this.$t(`creative.${d.type}`),
        value: d.id
      }))
      widgetFilters.unshift({
        label: `(${this.$t('Campaign Offer Flyout')})`,
        value: -1
      })

      return [
        {
          key: 'status',
          label: this.$t('Status'),
          type: 'select',
          placeholder: this.$t('Choose a status'),
          multiple: true,
          options: [
            {label: this.$t('Active'), value: 'active'},
            {label: this.$t('Disabled'), value: 'disabled'}
          ]
        },
        {
          key: 'type',
          label: this.$t('Type'),
          type: 'select',
          placeholder: this.$t('Choose a type'),
          multiple: true,
          options: _.map(this.segmentTypeLabelMap, (label, value) => ({
            label,
            value
          }))
        },
        {
          key: 'option',
          label: this.$t('Option'),
          type: 'select',
          placeholder: this.$t('Choose an option'),
          multiple: true,
          options: _.map(this.segmentOptionMap, (label, value) => ({
            label,
            value
          }))
        },
        {
          key: 'widget',
          label: this.$t('Widget'),
          type: 'select',
          placeholder: this.$t('Choose a widget'),
          multiple: true,
          options: widgetFilters
        },
        {
          key: 'offer',
          label: this.$t('Offer'),
          type: 'select',
          placeholder: this.$t('Choose an offer'),
          multiple: true,
          options: offerFilters
        }
      ]
    },
    segmentTypeLabelMap() {
      return  {
        'display-none': this.$t('campaignEdit.segment.type.hideOffers'),
        'exclude': this.$t('campaignEdit.segment.type.doNotTarget'),
        'ignore-user': this.$t('campaignEdit.segment.type.ignoreUser'),
        'exit-intent': this.$t('campaignEdit.segment.type.exitIntent'),
        'include': this.$t('campaignEdit.segment.type.standard')
      }
    },
    campaignOptions() {
      return this.campaigns.map(camp => {
        return {
          value: camp.id,
          label: camp.name
        }
      })
    },
    segmentOptionMap() {
      return {
        overrideDnt: this.$t('Override DNT'),
        overrideGenius: this.$t('Override Algorithms'),
        isUpsell: this.$t('Upsell'),
        sessionStickiness: this.$t('Sticky')
      }
    }
  },
  methods: {
    setFilterFromQuery() {
      if (this.$route.query) {
        this.appliedFilters = {}
        Object.keys(this.$route.query).forEach((k) => {
          if (this.$route.query[k]) {
            if (k === 'type' || k === 'status' || k === 'option') {
              if (Array.isArray(this.$route.query[k])) {
                this.appliedFilters[k] = this.$route.query[k]
              } else {
                this.appliedFilters[k] = [this.$route.query[k]]
              }
            } else if (k === 'widget' || k === 'offer'){
              if (Array.isArray(this.$route.query[k])) {
                this.appliedFilters[k] = this.$route.query[k].map(id => parseInt(id))
              } else {
                this.appliedFilters[k] = [parseInt(this.$route.query[k])]
              }
            } else {
              this.appliedFilters[k] = this.$route.query[k]
            }
          } else {
            this.appliedFilters[k] = null
          }
        })
      }
      if (_.isEmpty(this.$route.query)) {
        this.appliedFilters = {}
        this.appliedFilters.status = ['active']
      }

      this.$router.replace({ name: 'CampaignEditSegmentationList', query: this.appliedFilters})
      .catch(this.duplicateRouteCatch)
    },
    showCopyModal(segment) {
      this.copySegmentId = segment.id
      this.copySegmentName = segment.name
      this.copySegmentCampaignId = this.campaign.id
    },
    updateLimit(limit) {
      this.setTableLimit('tableLimit', limit)
      this.limit = limit
      this.offset = 0
    },
    offerIdsLength(segment) {
      return segment.offerIds && segment.offerIds.length
    },
    getWidgetName(widgetId) {
      const widget = this.widgets.find(w => w.id === widgetId)
      return widget && widget.name
    },
    getSegmentUrl(row) {
      return `/#/campaigns/${this.campaign.id}/segmentation/segment/${row.id}`
    },
    goToWidget(row) {
      const widgetId = row.widgetId === -1
        ? this.offerFlyoutId
        : row.widgetId
      return this.$router.push(`/creative/index.html#!/revisions/${widgetId}`)
    },
    editSegment(segment) {
      this.$router.push('segment/' + segment.id)
    },
    resetPagination() {
      this.offset = 0
    },
    setQuery(query) {
      this.query = query
      this.resetPagination()
    },
    async handleBulkAction(type) {
      const count = this.selectedIds.length
      const actionLabel = this.$t('campaignEdit.segment.changeSegments', { type: _.capitalize(type), count })
      const confirm = await this.$ui.confirm({
        title: actionLabel,
        content: this.$t('campaignEdit.segment.confirmChangeSegments', {type, count}),
        primaryActionLabel: actionLabel,
        primaryActionType: type === 'delete' ? 'destructive' : 'primary',
      })
      if (!confirm) {return}

      // console.log(this.selectedIds)
      if (type === 'disable') {
        NProgress.start()
        this.saving = true
        this.api.disableSegments(this.campaign.id, this.selectedIds)
          .then(() => {
            this.$ui.toast({ title: this.$t('Saved') })
            this.selectedIds.forEach(id => {
              const seg = this.segments.find(s => s.id === id)
              if (seg) seg.status = 'disabled'
            })
          })
          .catch(this.apiCatch)
          .finally(() => {
            NProgress.done()
            this.saving = false
            this.selectedIds = []
          })
      }
      if (type === 'enable') {
        NProgress.start()
        this.saving = true
        this.api.enableSegments(this.campaign.id, this.selectedIds)
          .then(() => {
            this.$ui.toast({ title: this.$t('Saved') })
            this.selectedIds.forEach(id => {
              const seg = this.segments.find(s => s.id === id)
              if (seg) seg.status = 'active'
            })
          })
          .catch(this.apiCatch)
          .finally(() => {
            this.saving = false
            NProgress.done()
            this.selectedIds = []
          })
      }
    },
    toBulkEditor() {
      this.$router.push({ name: 'SegmentationBulkEdit', props: { campaign: this.campaign }, query: { id: this.selectedIds } })
    },
    setAppliedFilters(filters) {
      this.appliedFilters = filters
      this.resetPagination()
      this.updateUrl()
    },
    updateUrl() {
      this.$router.push({ name: 'CampaignEditSegmentationList', query: this.appliedFilters})
      .catch(this.duplicateRouteCatch)
    },
    getSegmentOptions(segment) {
      const options = []
      for (const key in this.segmentOptionMap) {
        if (segment[key]) {
          options.push({
            label: this.segmentOptionMap[key],
            value: key
          })
        }
      }
      return options
    },
    async toggleSegment(segment) {
      const enable = segment.status !== 'active'
      const confirm = await this.$ui.confirm({
        title: this.$t('Toggle Segment'),
        content: (
          enable ?
          this.$t('Are you sure you want to enable segment: ') :
          this.$t('Are you sure you want to disable segment: ')
         ) + segment.name,
        primaryActionLabel: enable ? this.$t('Enable') : this.$t('Disable'),
        primaryActionType: 'destructive'
      })
      if (!confirm) {
        return 
      }
      NProgress.start()
      this.saving = true
      this.api.toggleSegment(segment.id, enable, this.campaign.id)
        .then(() => {
          const seg = this.segments.find(s => s.id === segment.id)
          seg.status = (enable ? 'active' : 'disabled')
        })
        .catch(this.apiCatch)
        .finally(() => {
          NProgress.done()
          this.saving = false
        })
    },
    async deleteSegment(segment) {
      const confirm = await this.$ui.confirm({
        title: this.$t('Delete Segment'),
        content: this.$t('Are you sure you want to delete segment', {value: segment.name}),
        primaryActionLabel: this.$t('Delete'),
        primaryActionType: 'destructive'
      })
      if (!confirm) {
        return 
      }
      NProgress.start()
      this.saving = true
      this.api.deleteSegment(segment.id, this.campaign.id) 
        .then(() => {
          this.segments = this.segments.filter(s => s.id !== segment.id)
        })
        .catch(this.apiCatch)
        .finally(() => {
          this.saving = false
          NProgress.done()
        })
    },
    async createNewSegment() {
      const newName = await this.$ui.prompt({
        title: this.$t('Create Segment'),
        content: this.$t('Enter New Segment Name'),
        primaryActionLabel: this.$t('Create'),
        inputPlaceholder: this.$t('Segment Name')
      })
      if (!newName || !newName.trim()) {
        return 
      }
      NProgress.start()
      this.saving = true
      this.api.createSegment(newName.trim(), this.campaign.id)
        .then((response) => {
          if (response) {
            this.$router.push('segment/' + response)
          }
        })
        .catch(this.apiCatch)
        .finally(() => {
          NProgress.done()
          this.saving = false
        })
    },
    async copySegment(goToEdit) {
      NProgress.start()
      this.saving = true
      this.api.copySegment(this.copySegmentName, this.copySegmentId, this.copySegmentCampaignId)
        .then((createdSeg) => {
          if (this.copySegmentCampaignId === this.campaign.id) {
            this.segments.push(createdSeg)
            this.$ui.toast({ title: 'Saved' })
          }
          if (goToEdit) {
            this.$router.push('/campaigns/' + createdSeg.campaignId + '/segmentation/segment/' + createdSeg.id)
          }
          this.copySegmentId = null
        })
        .catch(this.apiCatch)
        .finally(() => {  
          this.saving = false 
          NProgress.done()
        })
    },
    showOffers(row) {
      const offers = row.offerIds.map(id => this.offers.find(d => d.id === id))
      if (offers.length) {
        const h = this.$createElement
        this.$ui.alert({
          title: this.$t(`campaignEdit.segment.offersInSegment`, {value: row.name }),
          content: h('List', offers.map(d => {
            return h('ListItem', [
              h('a', {
                attrs: {
                  href: `/#/campaigns/${this.campaign.id}/offers/edit/${d.id}`,
                  target: '_blank'
                }
              }, d.label),
              h('Caption', d.description)
            ])
          }))
        })
      }
    },
    clearActiveIds() {
      this.offers.forEach(o => {
        o.isActive = false
      })
    }
  },
  components: {
    CampaignEditHeader
  }
}

</script>
